import * as React from "react";
import { motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";
import { isPdfRenderMode } from "@/lib/pdfMode";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  y?: number;
  as?: "div" | "section" | "li" | "figure";
}

const EASE = [0.22, 1, 0.36, 1] as const;

export const Reveal: React.FC<RevealProps> = ({
  children,
  className,
  delay = 0,
  y = 24,
}) => {
  // PDF/print must show all content without waiting for scroll into view.
  if (isPdfRenderMode()) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

interface OverlineProps {
  children: React.ReactNode;
  className?: string;
}

export const Overline: React.FC<OverlineProps> = ({ children, className }) => (
  <span
    className={cn(
      "block text-[0.7rem] md:text-xs uppercase tracking-[0.28em] text-charcoal-muted",
      className,
    )}
  >
    {children}
  </span>
);

interface CountUpProps {
  value: number;
  duration?: number;
  className?: string;
  suffix?: string;
  formatter?: (n: number) => string;
}

export const CountUp: React.FC<CountUpProps> = ({
  value,
  duration = 1.3,
  className,
  suffix = "",
  formatter,
}) => {
  const pdfMode = isPdfRenderMode();
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, margin: "-40px", amount: pdfMode ? 0 : undefined });
  const [display, setDisplay] = React.useState(pdfMode ? value : 0);

  React.useEffect(() => {
    if (pdfMode) {
      setDisplay(value);
      return;
    }
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, pdfMode]);

  return (
    <span ref={ref} className={className}>
      {formatter ? formatter(display) : display}
      {suffix}
    </span>
  );
};
